import { useEffect, useRef, useState } from "react";

/**
 * PAY-E1 / US 1.8 — bounded client-side fallback poller.
 *
 * While the Stripe PaymentIntent is in a non-terminal state (`processing`, async method awaiting a
 * webhook), the Booker cannot rely on PAY-E2 having flipped `Payment.success` yet. This hook re-reads
 * the payment status (via the injected `fetchStatus`, backed by the US 1.6 endpoint) on a **bounded
 * exponential backoff** until it observes a terminal status or times out. It never writes state — the
 * authority to confirm stays server-side (PAY-E2 / `handlePaymentSuccess`).
 *
 * `fetchStatus` is injected (rather than calling tRPC directly) so the hook is trivially testable and
 * reusable by both the inline Booker step and the `/payment/[uid]` fallback.
 */
export type PollableStatus = "payable" | "paid" | "refunded" | "not_payable";
export type TerminalStatus = Exclude<PollableStatus, "payable">;

const TERMINAL: ReadonlySet<PollableStatus> = new Set<PollableStatus>(["paid", "refunded", "not_payable"]);

export interface UseStripePaymentStatusPollingOptions {
  /** Only poll while true (e.g. the component is in its "validating" state). */
  enabled: boolean;
  /** Reads the current payment status. Rejections are swallowed and treated as "keep polling". */
  fetchStatus: () => Promise<PollableStatus>;
  /** Called once when a terminal status is observed. */
  onResolved: (status: TerminalStatus) => void;
  /** Called once if we give up after `maxAttempts` without a terminal status. */
  onTimeout?: () => void;
  /** Hard cap on attempts before timing out. Default 8. */
  maxAttempts?: number;
  /** Base backoff delay in ms. Default 2000. */
  baseDelayMs?: number;
  /** Backoff ceiling in ms. Default 15000. */
  maxDelayMs?: number;
}

export interface UseStripePaymentStatusPollingResult {
  attempts: number;
  isPolling: boolean;
  timedOut: boolean;
}

export function useStripePaymentStatusPolling({
  enabled,
  fetchStatus,
  onResolved,
  onTimeout,
  maxAttempts = 8,
  baseDelayMs = 2000,
  maxDelayMs = 15000,
}: UseStripePaymentStatusPollingOptions): UseStripePaymentStatusPollingResult {
  const [attempts, setAttempts] = useState(0);
  const [isPolling, setIsPolling] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  // Keep the latest callbacks/fetcher without re-arming the polling loop on every render.
  const fetchStatusRef = useRef(fetchStatus);
  const onResolvedRef = useRef(onResolved);
  const onTimeoutRef = useRef(onTimeout);
  fetchStatusRef.current = fetchStatus;
  onResolvedRef.current = onResolved;
  onTimeoutRef.current = onTimeout;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    setIsPolling(true);
    setTimedOut(false);
    setAttempts(0);

    const run = async () => {
      if (cancelled) return;
      attempt += 1;
      setAttempts(attempt);

      let status: PollableStatus | undefined;
      try {
        status = await fetchStatusRef.current();
      } catch {
        // Transient read error → treat as "keep polling" (do not surface).
        status = undefined;
      }
      if (cancelled) return;

      if (status && TERMINAL.has(status)) {
        setIsPolling(false);
        onResolvedRef.current(status as TerminalStatus);
        return;
      }

      if (attempt >= maxAttempts) {
        setIsPolling(false);
        setTimedOut(true);
        onTimeoutRef.current?.();
        return;
      }

      const delay = Math.min(maxDelayMs, baseDelayMs * 2 ** (attempt - 1));
      timer = setTimeout(run, delay);
    };

    // First read almost immediately; subsequent reads back off.
    timer = setTimeout(run, 0);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      setIsPolling(false);
    };
  }, [enabled, maxAttempts, baseDelayMs, maxDelayMs]);

  return { attempts, isPolling, timedOut };
}
